import { useRef, useState, type FormEvent } from 'react';
import { api, apiFetch, tokenStore, type ApiError } from '../api/client';
import { megabytes, useLimits } from '../api/limits';

interface UploadSession {
  id: string;
  chunkSize: number;
  totalChunks: number;
  receivedChunks: number;
  missingChunks: number[];
  complete: boolean;
}

interface AttachedVideo {
  contentItemId: string;
  versionId: string;
  title: string;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
  processedBy: string;
  limitations: string[];
}

/** Sent one at a time so a slow connection is not asked to hold several. */
async function putChunk(uploadId: string, index: number, blob: Blob): Promise<UploadSession> {
  const response = await apiFetch(`/admin/media/uploads/${uploadId}/chunks/${index}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${tokenStore.access ?? ''}`,
      'Content-Type': 'application/octet-stream',
    },
    body: blob,
  });
  const payload = await response.json();
  if (!response.ok) throw { ...payload, status: response.status } as ApiError;
  return payload as UploadSession;
}

/**
 * Chunked, resumable video upload (BRD FR-VID-001).
 *
 * The file is sliced in the browser and sent chunk by chunk, so a 5 GB upload
 * never has to succeed in one request and a failure part-way through can be
 * resumed rather than restarted — the server is asked which chunks it is still
 * missing and only those are re-sent.
 */
export function VideoUpload({
  onDone,
  onError,
}: {
  onDone: (video: AttachedVideo) => void;
  onError: (error: ApiError) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(0);
  const [total, setTotal] = useState(0);
  const [stage, setStage] = useState<string>('');
  const limits = useLimits();
  const resumable = useRef<{ id: string; file: File; chunkSize: number } | null>(null);

  async function sendChunks(uploadId: string, file: File, chunkSize: number, indexes: number[]) {
    for (const index of indexes) {
      const start = index * chunkSize;
      const blob = file.slice(start, Math.min(start + chunkSize, file.size));
      const state = await putChunk(uploadId, index, blob);
      setSent(state.receivedChunks);
    }
  }

  async function start(event: FormEvent) {
    event.preventDefault();
    const file = input.current?.files?.[0];
    if (!file) return;

    setBusy(true);
    setSent(0);
    try {
      setStage('Opening the upload…');
      const session = await api<UploadSession>('/admin/media/uploads', {
        method: 'POST',
        body: JSON.stringify({
          title: titleRef.current?.value?.trim() || file.name,
          originalName: file.name,
          mimeType: file.type || 'video/mp4',
          sizeBytes: file.size,
        }),
      });
      resumable.current = { id: session.id, file, chunkSize: session.chunkSize };
      setTotal(session.totalChunks);

      setStage('Sending…');
      await sendChunks(
        session.id,
        file,
        session.chunkSize,
        Array.from({ length: session.totalChunks }, (_, index) => index),
      );

      setStage('Assembling…');
      await api(`/admin/media/uploads/${session.id}/complete`, { method: 'POST', body: '{}' });

      setStage('Reading the video…');
      const video = await api<AttachedVideo>('/admin/media/videos', {
        method: 'POST',
        body: JSON.stringify({ uploadId: session.id }),
      });

      resumable.current = null;
      setStage('');
      if (input.current) input.current.value = '';
      if (titleRef.current) titleRef.current.value = '';
      onDone(video);
    } catch (caught) {
      setStage('');
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  /** Ask the server what is missing and send only that. */
  async function resume() {
    const pending = resumable.current;
    if (!pending || busy) return;

    setBusy(true);
    try {
      setStage('Checking what is missing…');
      const state = await api<UploadSession>(`/admin/media/uploads/${pending.id}`);
      setTotal(state.totalChunks);
      setSent(state.receivedChunks);

      setStage('Resuming…');
      await sendChunks(pending.id, pending.file, pending.chunkSize, state.missingChunks);
      await api(`/admin/media/uploads/${pending.id}/complete`, { method: 'POST', body: '{}' });

      setStage('Reading the video…');
      const video = await api<AttachedVideo>('/admin/media/videos', {
        method: 'POST',
        body: JSON.stringify({ uploadId: pending.id }),
      });
      resumable.current = null;
      setStage('');
      onDone(video);
    } catch (caught) {
      setStage('');
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  const percent = total > 0 ? Math.round((sent / total) * 100) : 0;

  return (
    <form className="tile" onSubmit={start}>
      <div className="tile__label">Upload a video</div>

      <input
        ref={titleRef}
        className="field__input"
        placeholder="Lesson title (optional)"
        style={{ margin: '10px 0 6px' }}
      />
      <input
        ref={input}
        className="field__input"
        type="file"
        accept="video/mp4,video/quicktime,video/webm"
        style={{ marginBottom: 10 }}
        required
      />

      {busy || stage ? (
        <>
          <div className="bar" aria-hidden="true">
            <i style={{ width: `${percent}%` }} />
          </div>
          <p className="tile__note">
            {stage} {total > 0 ? `${sent} of ${total} chunks` : ''}
          </p>
        </>
      ) : (
        <ul className="tile__rules">
          <li>
            <strong>Up to {megabytes(limits?.upload.maxVideoMb ?? 5120)}</strong> per video.
          </li>
          <li>MP4 or WebM. Duration and frame size are read from the file itself.</li>
          <li>
            Sent in pieces, so a dropped connection resumes where it stopped rather than
            starting again.
          </li>
        </ul>
      )}

      <div className="row">
        <button className="btn btn--sm" type="submit" disabled={busy}>
          {busy ? 'Uploading…' : 'Upload video'}
        </button>
        {resumable.current && !busy ? (
          <button className="btn btn--outline btn--sm" type="button" onClick={() => void resume()}>
            Resume
          </button>
        ) : null}
      </div>
    </form>
  );
}
